// Seed: the site's working grade–recovery curve, one per concentrate metal
// (paul-call.A1 #251). Bunker Hill supplied no design flotation test work —
// every curve is `[syn]`, its anchors constructed through the seeded
// design point (the plant's own `rd-kpi-<metal>-grade` / `-rec` design
// figures — PFS §7.1 design row, per the seed-retie ruling #258) at the
// PFS design grind P80 of 74 µm (`rd-spec-p80` design field). The deck's
// design point (Zn 90.0%/52.0%, Pb 87.0%/65.0%, Ag 94.0%/32.0 oz/t) and
// grind P80 of 75 µm are superseded — carried only in this header and the
// `CURVE_PROVENANCE` note below, not in the anchors. See
// docs/schema/grade-recovery-curve.md § Seed for the reasoning behind every
// number.

import type { GradeRecoveryCurve } from '@/versions/v1/schema/grade-recovery-curve';

const CURVE_PROVENANCE =
  '[syn] — the site supplied no design flotation test work; anchors constructed through the seeded design point (PFS §7.1) at the seeded P80 (deck design point/P80 superseded — see file header)';

/** Paul's call: "target recovery is typically 85 % and ideally the mid-90s" —
 * one site-wide target-recovery range, seeded once and shared by every
 * metal's curve. */
const TARGET_RECOVERY = { lo: 85, hi: 95 };

/** Design P80 — the PFS design grind size (`rd-spec-p80` design field, 74
 * µm), per the seed-retie ruling (#258, MS-N25 closed). The deck's design
 * grind size (75 µm, Module 1 §8.2 startup table) is superseded — see the
 * file header. One grinding circuit feeds all three flotation
 * concentrates, so all three curves are produced at the same grind size. */
const DESIGN_P80_UM = 74;

export const WORKING_CURVES: readonly GradeRecoveryCurve[] = [
  {
    id: 'wc-zn',
    metal: 'Zn',
    grindSizeP80Um: DESIGN_P80_UM,
    // Design point (rd-kpi-zn-grade design 58.0 % · rd-kpi-zn-rec design
    // 85.1 %, PFS §7.1) sits exactly on the curve by construction (Q2
    // probe). Deck design point (52.0 % / 90.0 %) superseded.
    anchors: [
      { recoveryPct: 70, gradePct: 63 },
      { recoveryPct: 80, gradePct: 60 },
      { recoveryPct: 85.1, gradePct: 58.0 },
      { recoveryPct: 92, gradePct: 54 },
      { recoveryPct: 97, gradePct: 49 },
    ],
    // Paul's call: Zn concentrate sits "a lot higher" than Cu's mid-20s to
    // low-30s. [syn] band around the seeded design grade (58.0 %).
    smelterGradeBand: { lo: 54, hi: 64 },
    targetRecovery: TARGET_RECOVERY,
    // [syn] ~1.6x the seeded shift-to-shift grade noise (rs-zn-grade
    // sigma 0.6) — wide enough that shift noise averaged into a
    // production-day mean does not itself flag as off-curve.
    tolerance: { gradePctAbs: 1.0 },
    provenance: CURVE_PROVENANCE,
  },
  {
    id: 'wc-pb',
    metal: 'Pb',
    grindSizeP80Um: DESIGN_P80_UM,
    // Design point (rd-kpi-pb-grade design 67.0 % · rd-kpi-pb-rec design
    // 88.2 %, PFS §7.1) sits exactly on the curve by construction. Deck
    // design point (65.0 % / 87.0 %) superseded.
    anchors: [
      { recoveryPct: 65, gradePct: 73 },
      { recoveryPct: 75, gradePct: 70.5 },
      { recoveryPct: 88.2, gradePct: 67.0 },
      { recoveryPct: 93, gradePct: 64 },
      { recoveryPct: 97, gradePct: 60 },
    ],
    // [syn] band around the seeded design grade (67.0 %), "a lot higher"
    // than Cu per Paul's call.
    smelterGradeBand: { lo: 62, hi: 74 },
    targetRecovery: TARGET_RECOVERY,
    // [syn] ~1.25x the seeded shift-to-shift grade noise (rs-pb-grade
    // sigma 0.8).
    tolerance: { gradePctAbs: 1.0 },
    provenance: CURVE_PROVENANCE,
  },
  {
    id: 'wc-ag',
    metal: 'Ag',
    grindSizeP80Um: DESIGN_P80_UM,
    // Design point (rd-kpi-ag-grade design 12.13 oz/t · rd-kpi-ag-rec
    // design 84.2 %, PFS §7.1) sits exactly on the curve by construction.
    // Deck design point (32.0 oz/t / 94.0 %) superseded. Ag grade is oz/t,
    // not a smelter-contract percentage — Paul's Cu/Zn/Pb percentage
    // comparison does not carry over; the band is [syn] around the seeded
    // design figure instead.
    anchors: [
      { recoveryPct: 70, gradePct: 14.5 },
      { recoveryPct: 78, gradePct: 13.3 },
      { recoveryPct: 84.2, gradePct: 12.13 },
      { recoveryPct: 90, gradePct: 11.0 },
      { recoveryPct: 95, gradePct: 9.5 },
    ],
    smelterGradeBand: { lo: 10.0, hi: 13.0 },
    targetRecovery: TARGET_RECOVERY,
    // [syn] tightened from the deck-basis 1.0 oz/t to 0.4 oz/t — the
    // seeded design grade shrank ~2.6x (32.0 → 12.13 oz/t) under the PFS
    // basis, so an unscaled absolute tolerance would have gone
    // disproportionately loose relative to the curve's own grade span;
    // still wide enough to absorb the seeded Ag drift shape's per-shift
    // step (rs-ag-grade "six strictly falling shifts >=0.15 oz/t") without
    // every shift flagging off-curve on its own.
    tolerance: { gradePctAbs: 0.4 },
    provenance: CURVE_PROVENANCE,
  },
];

export const WORKING_CURVE_BY_METAL: Readonly<Record<'Zn' | 'Pb' | 'Ag', GradeRecoveryCurve>> = {
  Zn: WORKING_CURVES[0]!,
  Pb: WORKING_CURVES[1]!,
  Ag: WORKING_CURVES[2]!,
};
